import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
type Phase = 'baseline-collection' | 'role-interface-definition' | 'structural-interface-snapshot' | 'context-localization' | 'full-diagnostic';
export function WorkRelationshipDecode() {
  const navigate = useNavigate();
  const [isAuthenticated] = useState(true);
  const [hasBaseline] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<Phase>(hasBaseline ? 'role-interface-definition' : 'baseline-collection');
  const [showExplanation, setShowExplanation] = useState(false);
  const [showWhatChanged, setShowWhatChanged] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState('');
  // Baseline data
  const [baselineData, setBaselineData] = useState({
    dateOfBirth: '1990-01-15',
    timeOfBirth: '',
    timeKnown: 'not-sure',
    placeOfBirth: '',
    country: '',
    year: '1990',
    month: '01',
    day: '15',
    hour: '12',
    minute: '00',
    period: 'AM'
  });
  // Role interface definition (replaces partner baseline)
  const [roleInterface, setRoleInterface] = useState({
    relationshipType: '',
    interactionFrequency: '',
    powerImpact: ''
  });
  // Context localization
  const [contextData, setContextData] = useState({
    frictionZones: [] as string[],
    internalState: '',
    contextNote: ''
  });
  // Structural interface snapshot
  const structuralSnapshot = {
    userWorkPattern: 'You operate as a system stabilizer at work. You naturally move toward building structure, organizing complexity, and creating clarity before executing. You prefer autonomy over constant guidance, depth over speed, and behind-the-scenes architecture over high-visibility performance.',
    roleActivation: 'Around this manager role, your system shifts into heightened vigilance. You become more contained, more precise, and less spontaneous. The autonomy you naturally operate from tightens. You start tracking their expectations more closely than your own judgment. Your internal processing slows because you are running two systems simultaneously: your own and your model of theirs.',
    compensationStrategy: 'Your system compensates by over-functioning cognitively. You prepare more than necessary, anticipate questions before they are asked, and build structure to prevent ambiguity. This creates the appearance of high performance but costs internal coherence. You are managing their perception more than executing from your center.',
    invisibleCost: 'This interaction quietly costs you speed and confidence. Decisions that would normally be clear require more internal processing. Your natural pace slows. The energy spent managing the interface reduces the energy available for creative or strategic work. Over time, this creates a subtle but persistent drain.'
  };
  // Full diagnostic output
  const fullDiagnosticOutput = {
    patternRoleInterface: 'This dynamic forms because your system is wired for autonomy and internal structure, while this manager role introduces external evaluation and authority. Your pattern interprets authority as potential constraint, which triggers preemptive structure-building to maintain control. This is not about their management style — it is about how your system processes authority.',
    repeatingLoop: 'The loop: Authority presence → vigilance increase → over-preparation → temporary relief → pattern reinforcement. Each cycle makes the pattern more automatic. The over-functioning feels like competence, which rewards the pattern, which makes it harder to notice the cost.',
    authorityDistortion: 'Power distorts this interaction by making your system prioritize their perception over your own judgment. When authority is above you, your internal compass recalibrates to their expectations. This is not conscious compliance — it is structural adaptation. The cost is that your own clarity becomes secondary.',
    growthBreakdownBoundary: 'This dynamic caps you where autonomy and authority intersect. Roles that require high visibility under authority will trigger this pattern most intensely. Growth requires holding your own center under external evaluation — not managing perception. Breakdown occurs when the compensation strategy exhausts the system faster than it can recover.',
    whatChanged: {
      baseline: 'The baseline showed your structural work pattern: system stabilizer, autonomy preference, depth over speed, behind-the-scenes orientation.',
      roleInterface: 'Adding the role interface (manager, daily interaction, direct power impact) revealed how your pattern shifts: heightened vigilance, over-functioning, perception management, and the invisible cost to speed and confidence.',
      whyNotPersonalities: 'This is not about personalities. It is about how your system structurally responds to authority interfaces. The same pattern would activate with any manager role that introduces external evaluation and constraint.'
    }
  };
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: currentYear - 1919
  }, (_, i) => currentYear - i);
  const months = [{
    value: '01',
    label: 'January'
  }, {
    value: '02',
    label: 'February'
  }, {
    value: '03',
    label: 'March'
  }, {
    value: '04',
    label: 'April'
  }, {
    value: '05',
    label: 'May'
  }, {
    value: '06',
    label: 'June'
  }, {
    value: '07',
    label: 'July'
  }, {
    value: '08',
    label: 'August'
  }, {
    value: '09',
    label: 'September'
  }, {
    value: '10',
    label: 'October'
  }, {
    value: '11',
    label: 'November'
  }, {
    value: '12',
    label: 'December'
  }];
  const getDaysInMonth = (year: string, month: string) => {
    if (!year || !month) return 31;
    return new Date(parseInt(year), parseInt(month), 0).getDate();
  };
  const updateDateOfBirth = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const dateString = `${year}-${month}-${day}`;
      setBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: dateString
      }));
    } else {
      setBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: ''
      }));
    }
  };
  const convertTo24Hour = (hour: string, period: string): string => {
    let h = parseInt(hour);
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h.toString().padStart(2, '0');
  };
  const updateTimeOfBirth = (hour: string, minute: string, period: string) => {
    const hour24 = convertTo24Hour(hour, period);
    const timeString = `${hour24}:${minute}`;
    setBaselineData(prev => ({
      ...prev,
      hour,
      minute,
      period,
      timeOfBirth: timeString
    }));
  };
  const handleBaselineSubmit = () => {
    if (!baselineData.dateOfBirth || !baselineData.placeOfBirth || !baselineData.country) return;
    setIsGenerating(true);
    setGeneratingMessage('Saving your baseline...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('role-interface-definition');
    }, 1500);
  };
  const handleRoleInterfaceSubmit = () => {
    if (!roleInterface.relationshipType || !roleInterface.interactionFrequency || !roleInterface.powerImpact) return;
    setIsGenerating(true);
    setGeneratingMessage('Generating interface snapshot...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('structural-interface-snapshot');
    }, 2000);
  };
  const handleContextSubmit = () => {
    setIsGenerating(true);
    setGeneratingMessage('Integrating context...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('full-diagnostic');
    }, 2000);
  };
  const handleToggleFrictionZone = (zone: string) => {
    setContextData(prev => ({
      ...prev,
      frictionZones: prev.frictionZones.includes(zone) ? prev.frictionZones.filter(z => z !== zone) : [...prev.frictionZones, zone]
    }));
  };
  if (isGenerating) {
    return <>
        <Helmet data-id="element-4433">
          <title data-id="element-4434">
            Generating Work Relationship Diagnostic | Lazy Monk Labs
          </title>
        </Helmet>
        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-4435">
          <div className="text-center" data-id="element-4436">
            <div className="mb-6" data-id="element-4437">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5F5F5]" data-id="element-4438"></div>
            </div>
            <p className="text-lg text-[#F5F5F5]/70" data-id="element-4439">{generatingMessage}</p>
          </div>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-4440">
        <title data-id="element-4441">Work Relationship Decode | Lazy Monk Labs</title>
      </Helmet>
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-4442">
        <header data-id="element-4443">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-4444">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-4445">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-4446">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-4447" />
                <span className="text-lg font-medium tracking-tight" data-id="element-4448">
                  Lazy Monk Labs
                </span>
              </Link>
              <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-4449">
                Back to Decode
              </Link>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16 px-6" data-id="element-4450">
          <div className="max-w-2xl mx-auto" data-id="element-4451">
            {/* PHASE 1: Baseline Collection - Abbreviated */}
            {currentPhase === 'baseline-collection' && <div data-id="element-4452">
                <div className="mb-12" data-id="element-4453">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-4454">
                    Work Relationship Decode
                  </h1>
                  <p className="text-lg text-[#F5F5F5]/60 italic mb-4" data-id="element-4455">
                    A diagnostic of how your system behaves around a specific
                    work role
                  </p>
                  <p className="text-base text-[#F5F5F5]/70 leading-relaxed" data-id="element-4456">
                    This report maps how your internal pattern adapts, tightens,
                    or compensates when interfacing with a specific work
                    authority or role — without judging either side.
                  </p>
                </div>
                <div className="space-y-8 mb-12" data-id="element-4457">
                  <div data-id="element-4458">
                    <label className="block text-sm font-medium mb-3" data-id="element-4459">
                      Date of birth
                    </label>
                    {baselineData.dateOfBirth && <div className="mb-4 p-4 bg-[#111214] border border-[#2B2B2B] text-center" data-id="element-4460">
                        <div className="text-2xl font-medium" data-id="element-4461">
                          {months.find(m => m.value === baselineData.month)?.label}{' '}
                          {baselineData.day}, {baselineData.year}
                        </div>
                      </div>}
                    <div className="grid grid-cols-3 gap-3" data-id="element-4462">
                      <div data-id="element-4463">
                        <label htmlFor="month" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-4464">
                          Month
                        </label>
                        <select id="month" value={baselineData.month} onChange={e => updateDateOfBirth(baselineData.year, e.target.value, baselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-4465">
                          <option value="" data-id="element-4466">Month</option>
                          {months.map(m => <option key={m.value} value={m.value} data-id="element-4467">
                              {m.label}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-4468">
                        <label htmlFor="day" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-4469">
                          Day
                        </label>
                        <select id="day" value={baselineData.day} onChange={e => updateDateOfBirth(baselineData.year, baselineData.month, e.target.value)} disabled={!baselineData.month || !baselineData.year} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors disabled:opacity-30" data-id="element-4470">
                          <option value="" data-id="element-4471">Day</option>
                          {Array.from({
                        length: getDaysInMonth(baselineData.year, baselineData.month)
                      }, (_, i) => i + 1).map(d => <option key={d} value={d.toString().padStart(2, '0')} data-id="element-4472">
                              {d}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-4473">
                        <label htmlFor="year" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-4474">
                          Year
                        </label>
                        <select id="year" value={baselineData.year} onChange={e => updateDateOfBirth(e.target.value, baselineData.month, baselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-4475">
                          <option value="" data-id="element-4476">Year</option>
                          {years.map(y => <option key={y} value={y} data-id="element-4477">
                              {y}
                            </option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div data-id="element-4478">
                    <label htmlFor="placeOfBirth" className="block text-sm font-medium mb-2" data-id="element-4479">
                      Place of birth
                    </label>
                    <input type="text" id="placeOfBirth" value={baselineData.placeOfBirth} onChange={e => setBaselineData(prev => ({
                  ...prev,
                  placeOfBirth: e.target.value
                }))} placeholder="City or town" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-4480" />
                  </div>
                  <div data-id="element-4481">
                    <label htmlFor="country" className="block text-sm font-medium mb-2" data-id="element-4482">
                      Country
                    </label>
                    <input type="text" id="country" value={baselineData.country} onChange={e => setBaselineData(prev => ({
                  ...prev,
                  country: e.target.value
                }))} placeholder="Country" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-4483" />
                  </div>
                </div>
                <button onClick={handleBaselineSubmit} disabled={!baselineData.dateOfBirth || !baselineData.placeOfBirth || !baselineData.country} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-4484">
                  Continue
                </button>
              </div>}

            {/* PHASE 2: Role Interface Definition (CRITICAL - replaces partner baseline) */}
            {currentPhase === 'role-interface-definition' && <div data-id="element-4485">
                <div className="mb-12" data-id="element-4486">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-4487">
                    Who are you decoding?
                  </h1>
                </div>
                <div className="space-y-12 mb-12" data-id="element-4488">
                  <div data-id="element-4489">
                    <h2 className="text-lg font-medium mb-6" data-id="element-4490">
                      This person is primarily:
                    </h2>
                    <div className="space-y-3" data-id="element-4491">
                      {['My manager / boss / skip-level / senior leader', 'A colleague / peer / co-founder', 'Someone who reports to me'].map(type => <label key={type} className={`block border p-5 cursor-pointer transition-colors ${roleInterface.relationshipType === type ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-4492">
                          <input type="radio" name="relationshipType" value={type} checked={roleInterface.relationshipType === type} onChange={e => setRoleInterface(prev => ({
                      ...prev,
                      relationshipType: e.target.value
                    }))} className="sr-only" data-id="element-4493" />
                          <span className="text-base" data-id="element-4494">{type}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-4495">
                    <h2 className="text-lg font-medium mb-6" data-id="element-4496">
                      Interaction frequency
                    </h2>
                    <div className="space-y-3" data-id="element-4497">
                      {['Daily', 'Weekly', 'Occasionally', 'Only in high-stakes situations'].map(freq => <label key={freq} className={`block border p-5 cursor-pointer transition-colors ${roleInterface.interactionFrequency === freq ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-4498">
                          <input type="radio" name="interactionFrequency" value={freq} checked={roleInterface.interactionFrequency === freq} onChange={e => setRoleInterface(prev => ({
                      ...prev,
                      interactionFrequency: e.target.value
                    }))} className="sr-only" data-id="element-4499" />
                          <span className="text-base" data-id="element-4500">{freq}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-4501">
                    <h2 className="text-lg font-medium mb-3" data-id="element-4502">
                      Their decisions affect your work:
                    </h2>
                    <div className="space-y-3" data-id="element-4503">
                      {['Directly', 'Indirectly', 'Occasionally', 'Barely'].map(impact => <label key={impact} className={`block border p-5 cursor-pointer transition-colors ${roleInterface.powerImpact === impact ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-4504">
                            <input type="radio" name="powerImpact" value={impact} checked={roleInterface.powerImpact === impact} onChange={e => setRoleInterface(prev => ({
                      ...prev,
                      powerImpact: e.target.value
                    }))} className="sr-only" data-id="element-4505" />
                            <span className="text-base" data-id="element-4506">{impact}</span>
                          </label>)}
                    </div>
                  </div>
                </div>
                <button onClick={handleRoleInterfaceSubmit} disabled={!roleInterface.relationshipType || !roleInterface.interactionFrequency || !roleInterface.powerImpact} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-4507">
                  Generate Snapshot
                </button>
              </div>}

            {/* PHASE 3: Structural Interface Snapshot (WOW MOMENT) */}
            {currentPhase === 'structural-interface-snapshot' && <div data-id="element-4508">
                <div className="mb-12" data-id="element-4509">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-4510">
                    Work Relationship Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-4511">
                    Interface snapshot
                  </p>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4512">
                  <div className="flex items-start gap-4 mb-4" data-id="element-4513">
                    <span className="text-2xl" data-id="element-4514">🧱</span>
                    <div className="flex-1" data-id="element-4515">
                      <h2 className="text-xl font-medium mb-3" data-id="element-4516">
                        Your default pattern at work
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4517">
                        {structuralSnapshot.userWorkPattern}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4518">
                  <div className="flex items-start gap-4 mb-4" data-id="element-4519">
                    <span className="text-2xl" data-id="element-4520">⚙️</span>
                    <div className="flex-1" data-id="element-4521">
                      <h2 className="text-xl font-medium mb-3" data-id="element-4522">
                        What changes in you around this role
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4523">
                        {structuralSnapshot.roleActivation}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4524">
                  <div className="flex items-start gap-4 mb-4" data-id="element-4525">
                    <span className="text-2xl" data-id="element-4526">🔁</span>
                    <div className="flex-1" data-id="element-4527">
                      <h2 className="text-xl font-medium mb-3" data-id="element-4528">
                        How your system compensates
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4529">
                        {structuralSnapshot.compensationStrategy}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4530">
                  <div className="flex items-start gap-4 mb-4" data-id="element-4531">
                    <span className="text-2xl" data-id="element-4532">⚠️</span>
                    <div className="flex-1" data-id="element-4533">
                      <h2 className="text-xl font-medium mb-3" data-id="element-4534">
                        What this interaction quietly costs you
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4535">
                        {structuralSnapshot.invisibleCost}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 text-center" data-id="element-4536">
                  <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider" data-id="element-4537">
                    This snapshot describes your pattern — not their intent or
                    psychology
                  </p>
                </div>
                <div className="mb-12" data-id="element-4538">
                  <button onClick={() => setShowExplanation(!showExplanation)} className="flex items-center gap-2 text-sm text-[#F5F5F5]/70 hover:text-[#F5F5F5] transition-colors" data-id="element-4539">
                    <span data-id="element-4540">📘</span>
                    <span data-id="element-4541">How this snapshot is generated</span>
                    <span className="text-xs" data-id="element-4542">
                      {showExplanation ? '▼' : '▶'}
                    </span>
                  </button>
                  {showExplanation && <div className="mt-4 p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-4543">
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed mb-4" data-id="element-4544">
                        This snapshot combines your structural work pattern with
                        the role interface you are operating under.
                      </p>
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed mb-4" data-id="element-4545">
                        It does not analyze the other person's psychology.
                      </p>
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-4546">
                        It shows how your system adapts, compensates, or
                        constricts in this configuration.
                      </p>
                    </div>}
                </div>
                <div className="space-y-4" data-id="element-4547">
                  <button onClick={() => setCurrentPhase('context-localization')} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-4548">
                    🧩 Add context (optional)
                  </button>
                  <div className="text-center" data-id="element-4549">
                    <p className="text-xs text-[#F5F5F5]/50 mb-2" data-id="element-4550">
                      Optional · 3 quick questions
                    </p>
                  </div>
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-4551">
                    Save Report
                  </button>
                </div>
              </div>}

            {/* PHASE 4: Context Localization */}
            {currentPhase === 'context-localization' && <div data-id="element-4552">
                <div className="mb-12" data-id="element-4553">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-4554">
                    Add context
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-4555">
                    Optional · 3 quick questions
                  </p>
                </div>
                <div className="space-y-12 mb-12" data-id="element-4556">
                  <div data-id="element-4557">
                    <h2 className="text-lg font-medium mb-3" data-id="element-4558">
                      Where does this relationship strain the most?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-6" data-id="element-4559">
                      Select all that apply
                    </p>
                    <div className="space-y-3" data-id="element-4560">
                      {['Authority / decision-making', 'Feedback / evaluation', 'Pace and urgency', 'Ownership ambiguity', 'Emotional tone'].map(zone => <label key={zone} className={`block border p-5 cursor-pointer transition-colors ${contextData.frictionZones.includes(zone) ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-4561">
                          <input type="checkbox" value={zone} checked={contextData.frictionZones.includes(zone)} onChange={() => handleToggleFrictionZone(zone)} className="sr-only" data-id="element-4562" />
                          <span className="text-base" data-id="element-4563">{zone}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-4564">
                    <h2 className="text-lg font-medium mb-6" data-id="element-4565">
                      Around this person, you usually feel:
                    </h2>
                    <div className="space-y-3" data-id="element-4566">
                      {['Alert and contained', 'Tense or guarded', 'Over-responsible', 'Diminished or hesitant'].map(state => <label key={state} className={`block border p-5 cursor-pointer transition-colors ${contextData.internalState === state ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-4567">
                          <input type="radio" name="internalState" value={state} checked={contextData.internalState === state} onChange={e => setContextData(prev => ({
                      ...prev,
                      internalState: e.target.value
                    }))} className="sr-only" data-id="element-4568" />
                          <span className="text-base" data-id="element-4569">{state}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-4570">
                    <h2 className="text-lg font-medium mb-3" data-id="element-4571">
                      Anything important about this dynamic right now?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-4" data-id="element-4572">Optional</p>
                    <textarea value={contextData.contextNote} onChange={e => setContextData(prev => ({
                  ...prev,
                  contextNote: e.target.value
                }))} rows={3} maxLength={200} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors resize-none" placeholder="Optional..." data-id="element-4573" />
                    <p className="text-xs text-[#F5F5F5]/40 mt-2 text-right" data-id="element-4574">
                      {contextData.contextNote.length}/200
                    </p>
                  </div>
                </div>
                <button onClick={handleContextSubmit} disabled={!contextData.internalState} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-4575">
                  Apply Context
                </button>
              </div>}

            {/* PHASE 5: Full Diagnostic */}
            {currentPhase === 'full-diagnostic' && <div data-id="element-4576">
                <div className="mb-12" data-id="element-4577">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-4578">
                    Work Relationship Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-4579">Full Diagnostic</p>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4580">
                  <div className="flex items-start gap-4 mb-4" data-id="element-4581">
                    <span className="text-2xl" data-id="element-4582">🧠</span>
                    <div className="flex-1" data-id="element-4583">
                      <h2 className="text-xl font-medium mb-3" data-id="element-4584">
                        Why this dynamic forms
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4585">
                        {fullDiagnosticOutput.patternRoleInterface}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4586">
                  <div className="flex items-start gap-4 mb-4" data-id="element-4587">
                    <span className="text-2xl" data-id="element-4588">🔁</span>
                    <div className="flex-1" data-id="element-4589">
                      <h2 className="text-xl font-medium mb-3" data-id="element-4590">
                        The loop that keeps repeating
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4591">
                        {fullDiagnosticOutput.repeatingLoop}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4592">
                  <div className="flex items-start gap-4 mb-4" data-id="element-4593">
                    <span className="text-2xl" data-id="element-4594">🏗️</span>
                    <div className="flex-1" data-id="element-4595">
                      <h2 className="text-xl font-medium mb-3" data-id="element-4596">
                        How power distorts this interaction
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4597">
                        {fullDiagnosticOutput.authorityDistortion}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4598">
                  <div className="flex items-start gap-4 mb-4" data-id="element-4599">
                    <span className="text-2xl" data-id="element-4600">⚠️</span>
                    <div className="flex-1" data-id="element-4601">
                      <h2 className="text-xl font-medium mb-3" data-id="element-4602">
                        Where this dynamic caps you
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4603">
                        {fullDiagnosticOutput.growthBreakdownBoundary}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12" data-id="element-4604">
                  <button onClick={() => setShowWhatChanged(!showWhatChanged)} className="flex items-center gap-2 text-base font-medium text-[#F5F5F5] hover:text-[#F5F5F5]/80 transition-colors mb-6" data-id="element-4605">
                    <span data-id="element-4606">🔍</span>
                    <span data-id="element-4607">What changed after adding context</span>
                    <span className="text-xs" data-id="element-4608">
                      {showWhatChanged ? '▼' : '▶'}
                    </span>
                  </button>
                  {showWhatChanged && <div className="space-y-6" data-id="element-4609">
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-4610">
                        <h3 className="text-sm font-medium mb-4" data-id="element-4611">
                          What baseline showed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-4612">
                          {fullDiagnosticOutput.whatChanged.baseline}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-4613">
                        <h3 className="text-sm font-medium mb-4" data-id="element-4614">
                          What role interface revealed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-4615">
                          {fullDiagnosticOutput.whatChanged.roleInterface}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-4616">
                        <h3 className="text-sm font-medium mb-4" data-id="element-4617">
                          Why this is not about personalities
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-4618">
                          {fullDiagnosticOutput.whatChanged.whyNotPersonalities}
                        </p>
                      </div>
                    </div>}
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4619">
                  <div className="flex items-start gap-4 mb-4" data-id="element-4620">
                    <span className="text-2xl" data-id="element-4621">🔁</span>
                    <div className="flex-1" data-id="element-4622">
                      <h2 className="text-xl font-medium mb-3" data-id="element-4623">
                        Pattern Consistency Check
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-4624">
                        This reaction pattern appears with similar role
                        interfaces. The specific person changes, but the way
                        your system responds to this type of authority or power
                        dynamic remains structurally consistent. This is about
                        your system, not them.
                      </p>
                      <div className="space-y-4" data-id="element-4625">
                        <div data-id="element-4626">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-4627">
                            Other bosses / peers / reports
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-4628">
                            If you examine other relationships with similar role
                            structures, you will likely find the same internal
                            shifts. With other managers, the same vigilance and
                            over-functioning. With other peers, the same control
                            or withdrawal patterns. With other reports, the same
                            over-responsibility or distance. The people are
                            different, but your system's response to the role
                            interface is consistent.
                          </p>
                        </div>
                        <div data-id="element-4629">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-4630">
                            Similar power asymmetries
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-4631">
                            Across different organizations and contexts, the
                            same pattern activates when power dynamics are
                            similar. Authority above you triggers one response;
                            authority below you triggers another. The pattern is
                            not about the specific person's management style or
                            personality — it is about how your system
                            structurally processes power asymmetry.
                          </p>
                        </div>
                        <div data-id="element-4632">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-4633">
                            Repeated internal shifts
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-4634">
                            The same internal changes occur: heightened
                            vigilance, increased containment, perception
                            management, or withdrawal. These shifts are
                            automatic responses to the role interface, not
                            conscious choices. They repeat because they are
                            structural adaptations to authority configurations,
                            not reactions to individual personalities.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4635">
                  <div className="flex items-start gap-4 mb-4" data-id="element-4636">
                    <span className="text-2xl" data-id="element-4637">⚖️</span>
                    <div className="flex-1" data-id="element-4638">
                      <h2 className="text-xl font-medium mb-6" data-id="element-4639">
                        Where This Diagnosis Applies — and Where It Doesn't
                      </h2>
                      <div className="space-y-6" data-id="element-4640">
                        <div data-id="element-4641">
                          <h3 className="text-sm font-medium mb-3 text-[#F5F5F5]/80" data-id="element-4642">
                            This diagnosis explains:
                          </h3>
                          <ul className="space-y-2 text-sm text-[#F5F5F5]/70" data-id="element-4643">
                            <li data-id="element-4644">
                              • Why certain roles trigger specific internal
                              responses in you
                            </li>
                            <li data-id="element-4645">
                              • Why your behavior shifts around particular
                              authority configurations
                            </li>
                            <li data-id="element-4646">
                              • How your system adapts, compensates, or
                              constricts in this role interface
                            </li>
                            <li data-id="element-4647">
                              • What this dynamic quietly costs you over time
                            </li>
                          </ul>
                        </div>
                        <div data-id="element-4648">
                          <h3 className="text-sm font-medium mb-3 text-[#F5F5F5]/80" data-id="element-4649">
                            This diagnosis does NOT explain:
                          </h3>
                          <ul className="space-y-2 text-sm text-[#F5F5F5]/70" data-id="element-4650">
                            <li data-id="element-4651">
                              • The other person's intentions, psychology, or
                              motivations
                            </li>
                            <li data-id="element-4652">
                              • Their personality, management style, or
                              character
                            </li>
                            <li data-id="element-4653">
                              • Who is correct or incorrect in specific
                              conflicts
                            </li>
                            <li data-id="element-4654">
                              • What they should do differently or how they
                              should change
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4655">
                  <div className="flex items-start gap-4 mb-4" data-id="element-4656">
                    <span className="text-2xl" data-id="element-4657">🧭</span>
                    <div className="flex-1" data-id="element-4658">
                      <h2 className="text-xl font-medium mb-3" data-id="element-4659">
                        Integration Summary
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4660">
                        This is your system responding to a specific role
                        interface. When this type of authority or power dynamic
                        is present, your pattern shifts in predictable ways:
                        vigilance increases, containment tightens, or withdrawal
                        activates. The pattern repeats across similar role
                        configurations because it is a structural response to
                        power asymmetry, not a reaction to individual
                        personalities. The dynamic is not about who is right —
                        it is about how your system distorts under this
                        particular interface.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4 pt-8 border-t border-[#2B2B2B]" data-id="element-4661">
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-4662">
                    💾 Save Decode
                  </button>
                  <Link to="/report-detail?report=deep-work-decode" className="block w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors text-center" data-id="element-4663">
                    ➡️ Deep Work Decode
                  </Link>
                </div>
              </div>}
          </div>
        </main>
      </div>
    </>;
}